import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { Button, DialogActions, DialogContent } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";

import { useRestoreUserMutation } from "@/requests/user.mutations";
import { useUsersRestoreInfo, useUsersTableActions } from "@/stores/use-users-table";
import { UserType } from "@/types/user.types";

import { IdentityNoField } from "../userCreateDialog";

const formSchema = z
    .object({
        identityNo: z.string(),
        userType: z.nativeEnum(UserType),
        isIdentityNoRequired: z.boolean(),
    })
    .superRefine(({ identityNo, userType, isIdentityNoRequired }, ctx) => {
        if (userType === UserType.DRIVER) {
            if (isIdentityNoRequired && identityNo.length !== 11) {
                ctx.addIssue({
                    path: ["identityNo"],
                    message: "TC kimlik numarası 11 haneli olmalıdır.",
                    code: z.ZodIssueCode.custom,
                });
            }
        }
    });

export type UserRestoreForm = z.infer<typeof formSchema>;

function UserRestoreDialogForm() {
    const { t } = useTranslation();
    const { isPending, mutateAsync } = useRestoreUserMutation();
    const deletionInfo = useUsersRestoreInfo();
    const {
        formState: { isValid, errors },
        handleSubmit,
        register,
    } = useForm({
        defaultValues: {
            identityNo: "",
            userType: deletionInfo?.userType || UserType.DRIVER,
            isIdentityNoRequired: deletionInfo?.userType === UserType.DRIVER && !deletionInfo?.identityNo,
        },
        disabled: isPending,
        resolver: zodResolver(formSchema),
    });

    const { updateRestoreInfo } = useUsersTableActions();

    const handleConfirm = async (data: UserRestoreForm) => {
        if (isPending || !deletionInfo) return;
        try {
            const res = await mutateAsync({
                id: deletionInfo.id,
                identityNo: data.identityNo,
            });
            enqueueSnackbar(t(res.message || ""), { variant: "success" });
        } catch (error) {
            console.error("restore error:", error);
        }
        handleClose();
    };

    const handleClose = () => {
        updateRestoreInfo(null);
    };

    if (!deletionInfo) return null;

    return (
        <>
            {deletionInfo.userType === UserType.DRIVER && !deletionInfo.identityNo && (
                <DialogContent dividers>
                    <form autoComplete="off" onSubmit={handleSubmit(handleConfirm)}>
                        <IdentityNoField className="w-full" errors={errors} loading={isPending} register={register} />
                    </form>
                </DialogContent>
            )}
            <DialogActions>
                <Button disabled={isPending} autoFocus color="secondary" variant="outlined" onClick={handleClose}>
                    {t("buttons.cancel")}
                </Button>

                <LoadingButton
                    disabled={!isValid}
                    loading={isPending}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(handleConfirm)}
                >
                    {t("buttons.updateUserRestore")}
                </LoadingButton>
            </DialogActions>
        </>
    );
}

export default UserRestoreDialogForm;
